import { createContext, redirect } from 'react-router';
import type { AuthInfo } from '@/amplify/get-auth-info.ts';
import type { UserGroup } from '@/amplify/is-in-group.ts';
import { getRedirectLink } from '@/routes-utils/get-redirect-link.ts';
import { paths } from '@/routes-utils/paths.ts';

const knownGroups = [
  'Users',
  'Admins',
  'Enterprise',
] as const satisfies readonly UserGroup[];

export type SessionIdentity = {
  id: string;
  email: string;
  fullName: string;
  groups: readonly UserGroup[];
};

export type GuestSession = { kind: 'guest' };

export type IncompleteSession = {
  kind: 'incomplete';
  identity: SessionIdentity;
};

export type UserSession = {
  kind: 'user';
  identity: SessionIdentity;
  username: string;
};

export type AppSession = GuestSession | IncompleteSession | UserSession;

export type SessionTokenClaims = {
  idToken?: { payload?: Record<string, unknown> };
  accessToken?: { payload?: Record<string, unknown> };
};

export const sessionContext = createContext<AppSession>();

const groupsFromPayload = (raw: unknown): UserGroup[] => {
  if (!Array.isArray(raw)) {
    return [];
  }

  return raw.filter((group): group is UserGroup =>
    knownGroups.some((known) => known === group),
  );
};

export const sessionFromTokens = (
  tokens: SessionTokenClaims | null | undefined,
): AppSession => {
  const idPayload = tokens?.idToken?.payload;
  const sub = idPayload?.['sub'];
  if (!tokens || typeof sub !== 'string' || sub.length === 0) {
    return { kind: 'guest' };
  }

  const usernameValue = idPayload?.['preferred_username'];
  const username =
    typeof usernameValue === 'string' ? usernameValue : undefined;
  const emailValue = idPayload?.['email'];
  const nameValue = idPayload?.['name'];
  const identity: SessionIdentity = {
    id: sub,
    email: typeof emailValue === 'string' ? emailValue : '',
    fullName: (typeof nameValue === 'string' && nameValue) || username || '',
    groups: groupsFromPayload(tokens.accessToken?.payload?.['cognito:groups']),
  };

  if (!username) {
    return { kind: 'incomplete', identity };
  }

  return { kind: 'user', identity, username };
};

export const authInfoFromSession = (
  session: IncompleteSession | UserSession,
): AuthInfo => ({
  id: session.identity.id,
  email: session.identity.email,
  fullName: session.identity.fullName,
  username: session.kind === 'user' ? session.username : '',
  isAdmin: session.identity.groups.includes('Admins'),
  isEnterprise: session.identity.groups.includes('Enterprise'),
});

export const sessionHasGroup = (
  session: UserSession,
  groups: UserGroup[],
): boolean => session.identity.groups.some((group) => groups.includes(group));

export type ProtectedSessionOpts = {
  groups?: UserGroup[];
};

export const redirectForProtectedSession = (
  session: AppSession,
  request: Request,
  { groups }: ProtectedSessionOpts = {},
): Response | undefined => {
  const pathname = new URL(request.url).pathname;

  switch (session.kind) {
    case 'guest':
      return redirect(getRedirectLink(paths.login, pathname));
    case 'incomplete':
      return redirect(getRedirectLink(paths.signup, pathname));
    case 'user':
      if (groups && !sessionHasGroup(session, groups)) {
        return redirect(paths.home);
      }
      return undefined;
    default: {
      const _exhaustive: never = session;
      return _exhaustive;
    }
  }
};
